import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { ShareWeb } from '@one-thd/sui-icons';
import { bool, string } from 'prop-types';
import {
  Alert,
  Button,
  Popover,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import _noop from 'lodash/noop';
import logNewRelicCustom from '../../../utils/logNewRelicCustom';
import { useDigitalDataStore } from '../../../store';
import { ANALYTICS_EVENTS, NEW_RELIC_EVENTS } from '../../../constants';
import { getShareableUrl } from '../../../utils/queryParams';

const getCurrentUrl = () => {
  return typeof window !== 'undefined' ? window.location.href : '';
};

const ShareBuild = ({ title, isPackout, disabled }) => {
  const trackShareDesignEvent = useDigitalDataStore((state) => state.trackShareDesignEvent);
  const [open, setOpen] = useState(false);
  const [isTextCopied, setIsTextCopied] = useState(false);
  const anchorRef = useRef(null);
  const currentUrl = getShareableUrl();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl);
  };

  const handleCopyClick = () => {
    if (disabled) return;
    copyToClipboard();
    setIsTextCopied(true);
    trackShareDesignEvent(ANALYTICS_EVENTS.COPY_URL, currentUrl);
    logNewRelicCustom(NEW_RELIC_EVENTS.SHARE_DESIGN, { designUrl: currentUrl });
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsTextCopied(false);
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const id = open ? 'share-build-popover' : undefined;
  const wrapperClasses = classnames('', {
    'sui-mb-2 sui-inline-block sui-mt-2 hover:sui-cursor-pointer last:hover:sui-no-underline': !isPackout,
  });
  const buttonClasses = classnames('sui-flex', {
    'sui-mr-2 sui-flex': !isPackout,
    'sui-flex sui-flex-row sui-items-start sui-pt-4': isPackout,
  });

  return (
    <div
      className={wrapperClasses}
      data-component="ShareBuild"
    >

      <div
        ref={anchorRef}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        className={buttonClasses}
        aria-disabled={disabled}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
      >
        {isPackout ? (
          <>
            <span className="sui-pr-1">
              <ShareWeb color="brand" />
            </span>
            <Typography color="primary" variant="body-xs" weight="bold">
              Share
            </Typography>
          </>
        ) : (
          <>
            <span className="sui-pr-2">
              <ShareWeb size="regular" />
            </span>
            <span>
              <Typography color="primary" variant="body-base">Share</Typography>
            </span>
          </>
        )}
      </div>
      <Popover
        style={{ zIndex: 900 }}
        id={id}
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        placement="top-start"
        title={title}
      >
        {isTextCopied ? (
          <div className="sui-flex">
            <Alert status="success">Your link has been copied.</Alert>
          </div>
        ) : (
          <div className="sui-flex sui-items-center sui-grow">
            <span className="sui-pr-1 sui-flex sui-grow">
              <TextField
                onChange={_noop}
                id="share-build-link-textbox"
                value={currentUrl}
                fullWidth
              />
            </span>
            <span className="sui-pt-2">
              <Button onClick={handleCopyClick} variant="secondary" disabled={disabled}>
                Copy
              </Button>
            </span>
          </div>
        )}
      </Popover>
    </div>
  );
};

ShareBuild.propTypes = {
  title: string,
  isPackout: bool,
  disabled: bool,
};

ShareBuild.defaultProps = {
  title: 'Share Your Build',
  isPackout: false,
  disabled: false,
};

export default ShareBuild;
